/**
 * 🆕 [Phase v13.50 — Item 4]
 * HistoriqueCommentairesDialog — Dialog qui affiche le fil conversationnel
 * complet employé ⇄ propriétaire pour un punch donné.
 *
 * Design :
 *   - Ordre chronologique croissant (ancien → récent, style email thread / WhatsApp)
 *   - Bulles alignées à gauche pour l'employé, à droite pour le propriétaire
 *   - Couleurs distinctes : bleu clair (employé) / vert (propriétaire)
 *   - Timestamp formaté FR-CA (« 17 juillet 2026 à 14 h 32 »)
 *   - Badge statut de la demande (en attente / acceptée / refusée)
 *   - Marker « synthétisé » quand l'entrée provient de la rétrocompat legacy
 *   - Auto-scroll vers le bas au chargement (comportement Slack/WhatsApp)
 *
 * Ne modifie rien en base — read-only.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ClipboardList, Loader2, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/** Formate un ISO en FR-CA : « 17 juillet 2026 à 14 h 32 ». */
function formatDateFrCA(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d)) return String(iso);
    const dateStr = new Intl.DateTimeFormat('fr-CA', {
      day: 'numeric', month: 'long', year: 'numeric',
    }).format(d);
    const heure = d.toLocaleTimeString('fr-CA', {
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).replace(':', ' h ');
    return `${dateStr} à ${heure}`;
  } catch {
    return String(iso);
  }
}

/** Badge de statut avec libellé + couleur. */
function StatutBadge({ statut }) {
  if (!statut) return null;
  const map = {
    en_attente: { label: 'En attente', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
    acceptee: { label: 'Acceptée', cls: 'bg-green-100 text-green-800 border-green-300' },
    approuve: { label: 'Acceptée', cls: 'bg-green-100 text-green-800 border-green-300' },
    refusee: { label: 'Refusée', cls: 'bg-red-100 text-red-700 border-red-300' },
    refuse: { label: 'Refusée', cls: 'bg-red-100 text-red-700 border-red-300' },
  };
  const cfg = map[statut] || { label: statut, cls: 'bg-slate-100 text-slate-700 border-slate-300' };
  return (
    <Badge className={`text-[10px] font-medium border ${cfg.cls}`} data-testid={`statut-badge-${statut}`}>
      {cfg.label}
    </Badge>
  );
}

export function HistoriqueCommentairesDialog({ punchId, open, onOpenChange, punchLabel }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [entries, setEntries] = useState([]);
  const [synthetise, setSynthetise] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!open || !punchId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setEntries([]);
    (async () => {
      try {
        const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
        const res = await axios.get(
          `${API_URL}/api/punch/${punchId}/historique-commentaires`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        if (cancelled) return;
        setEntries(res.data?.entries || []);
        setSynthetise(!!res.data?.synthetise);
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || 'Erreur de chargement');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, punchId]);

  // Auto-scroll vers le bas quand les entries sont chargées
  useEffect(() => {
    if (!loading && entries.length > 0 && bottomRef.current) {
      requestAnimationFrame(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      });
    }
  }, [loading, entries.length]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] flex flex-col"
        data-testid="dialog-historique-commentaires"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-purple-600" />
            Historique des commentaires
          </DialogTitle>
          <DialogDescription>
            Fil conversationnel employé ⇄ propriétaire{punchLabel ? ` — ${punchLabel}` : ''}.
            Ordre chronologique, du plus ancien au plus récent.
          </DialogDescription>
        </DialogHeader>

        {synthetise && !loading && entries.length > 0 && (
          <div
            className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
            data-testid="banniere-synthetise"
          >
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              Ce punch est antérieur à v13.50. L&apos;historique est reconstitué à partir de
              la dernière demande enregistrée — les échanges intermédiaires ne sont pas
              conservés pour les anciens punchs.
            </span>
          </div>
        )}

        <div
          className="flex-1 overflow-y-auto space-y-3 pr-1"
          data-testid="historique-scroll-container"
        >
          {loading && (
            <div className="flex items-center justify-center py-8 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              <span>Chargement…</span>
            </div>
          )}

          {!loading && error && (
            <div
              className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              data-testid="historique-error"
            >
              <strong>Erreur :</strong> {error}
            </div>
          )}

          {!loading && !error && entries.length === 0 && (
            <p
              className="text-center text-sm text-slate-400 italic py-6"
              data-testid="historique-vide"
            >
              Aucun commentaire enregistré sur ce punch.
            </p>
          )}

          {!loading && !error && entries.map((e, idx) => {
            const isEmploye = e.auteur_role === 'employe'; // Cat H — `e.auteur_role` = donnée stockée sur l'entrée d'historique (empoyé vs admin), pas RBAC utilisateur connecté. Utilisé pour aligner la bulle à gauche/droite.
            return (
              <div
                key={e.id || idx}
                className={`flex ${isEmploye ? 'justify-start' : 'justify-end'}`}
                data-testid={`historique-entry-${idx}`}
                data-role={e.auteur_role}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2.5 shadow-sm ${
                    isEmploye
                      ? 'bg-blue-50 border border-blue-200 rounded-tl-sm'
                      : 'bg-emerald-50 border border-emerald-200 rounded-tr-sm'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span
                      className={`text-xs font-semibold ${
                        isEmploye ? 'text-blue-900' : 'text-emerald-900'
                      }`}
                    >
                      {e.auteur_nom || (isEmploye ? 'Employé' : 'Propriétaire')}
                      <span className={`ml-2 text-[10px] font-normal ${
                        isEmploye ? 'text-blue-500' : 'text-emerald-600'
                      }`}>
                        {isEmploye ? '· employé' : '· propriétaire'}
                      </span>
                    </span>
                    <StatutBadge statut={e.statut_associe} />
                  </div>
                  <p className="text-sm text-slate-900 whitespace-pre-wrap break-words">
                    {e.contenu || <em className="text-slate-400">(vide)</em>}
                  </p>
                  {e.heures_ajustees && (
                    <p className="mt-1 text-[11px] text-slate-500 border-t border-slate-100 pt-1">
                      Heures ajustées : <strong>{(e.heures_ajustees.heures_nettes ?? 0).toFixed(2)} h</strong>
                    </p>
                  )}
                  <p
                    className={`text-[10px] mt-1 ${
                      isEmploye ? 'text-blue-500' : 'text-emerald-600'
                    }`}
                  >
                    {formatDateFrCA(e.date)}
                    {e.synthetise && (
                      <span
                        className="ml-2 text-[10px] italic text-amber-700"
                        data-testid={`badge-synthetise-${idx}`}
                      >
                        · synthétisé
                      </span>
                    )}
                  </p>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default HistoriqueCommentairesDialog;
